import React, { useState, useEffect } from 'react';
import { XMarkIcon, ExclamationTriangleIcon, CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useToasts, ToastDescriptor } from '@/state/ToastContext';

const toneStyles: Record<string, { bg: string; icon: string; progress: string }> = {
  info: {
    bg: 'bg-white border-slate-200 text-slate-900',
    icon: 'bg-blue-100 text-blue-600',
    progress: 'bg-blue-500',
  },
  success: {
    bg: 'bg-white border-emerald-200 text-slate-900',
    icon: 'bg-emerald-100 text-emerald-600',
    progress: 'bg-emerald-500',
  },
  warning: {
    bg: 'bg-white border-amber-200 text-slate-900',
    icon: 'bg-amber-100 text-amber-600',
    progress: 'bg-amber-500',
  },
  error: {
    bg: 'bg-white border-rose-200 text-slate-900',
    icon: 'bg-rose-100 text-rose-600',
    progress: 'bg-rose-500',
  },
};

const toneIcon: Record<string, typeof InformationCircleIcon> = {
  info: InformationCircleIcon,
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  error: ExclamationTriangleIcon,
};

interface ToastItemProps {
  toast: ToastDescriptor;
  onDismiss: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onDismiss }) => {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const tone = toast.tone ?? 'info';
  const Icon = toneIcon[tone] ?? InformationCircleIcon;
  const styles = toneStyles[tone];

  useEffect(() => {
    if (toast.autoClose && typeof toast.autoClose === 'number' && toast.autoClose > 0) {
      const startTime = Date.now();
      const duration = toast.autoClose;

      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
        setProgress(remaining);

        if (remaining === 0) {
          clearInterval(interval);
        }
      }, 50);

      return () => clearInterval(interval);
    }
  }, [toast.autoClose]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 300);
  };

  return (
    <div
      className={`pointer-events-auto transform overflow-hidden rounded-xl border shadow-lg transition-all duration-300 ${
        styles.bg
      } ${
        isExiting 
          ? 'translate-x-[400px] opacity-0' 
          : 'translate-x-0 opacity-100'
      }`}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${styles.icon}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900">{toast.message}</p>
          {toast.detail && (
            <p className="mt-1 text-xs text-slate-600 leading-relaxed">{toast.detail}</p>
          )}
          {toast.action && (
            <button
              onClick={() => {
                toast.action!.onClick();
                handleDismiss();
              }}
              className={`mt-2 text-xs font-semibold transition hover:underline ${
                tone === 'info' ? 'text-blue-600 hover:text-blue-700' :
                tone === 'success' ? 'text-emerald-600 hover:text-emerald-700' :
                tone === 'warning' ? 'text-amber-600 hover:text-amber-700' :
                'text-rose-600 hover:text-rose-700'
              }`}
            >
              {toast.action.label} →
            </button>
          )}
        </div>
        <button
          type="button"
          className="flex-shrink-0 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          onClick={handleDismiss}
          aria-label="關閉通知"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
      {toast.autoClose && typeof toast.autoClose === 'number' && toast.autoClose > 0 && (
        <div className="h-1 w-full bg-slate-100">
          <div
            className={`h-full transition-all duration-100 ease-linear ${styles.progress}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

const ToastStack: React.FC = () => {
  const { toasts, dismissToast } = useToasts();

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed right-6 top-6 z-[60] flex w-full max-w-sm flex-col gap-3">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={dismissToast} />
      ))}
    </div>
  );
};

export default ToastStack;
